import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bot, Send, Loader2, Trash2, ShieldCheck, Gauge, Crown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { errToText, timeUntil } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const STARTERS = [
  "What can BLOQ Talk help me with?",
  "Explain crypto wallets in one paragraph.",
  "How does my BLOQ Score work?",
  "How do I spot a scam DM?",
];

function Bubble({ role, text }) {
  const isUser = role === "user";
  return (
    <div className={cn("flex w-full", isUser ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-4 py-2.5 text-[15px] leading-relaxed sm:max-w-[72%]",
          isUser
            ? "rounded-br-md bg-gradient-to-br from-bloq-mint/90 to-bloq-blue/90 text-bloq-bg"
            : "rounded-bl-md border border-white/8 bg-bloq-s2 text-white",
        )}
        data-testid={`assistant-msg-${role}`}
      >
        {text}
      </div>
    </div>
  );
}

export default function AssistantPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canUpgrade = (user?.plan || "free") === "free"; // only nudge free-plan users to upgrade
  const [text, setText] = useState("");
  const [confirmClear, setConfirmClear] = useState(false);
  const bottomRef = useRef(null);

  const { data, isLoading } = useQuery({
    queryKey: ["assistant-history"],
    queryFn: () => api.assistantHistory(50),
  });
  const items = data?.items || [];
  const quota = data?.quota_remaining; // int | null (null = no limit / unknown)
  const resetAt = data?.quota_reset_at; // exact UTC reset timestamp from the backend
  const outOfQuota = quota === 0;

  const sendMut = useMutation({
    mutationFn: (message) => api.assistantSend(message),
    onMutate: async (message) => {
      await qc.cancelQueries({ queryKey: ["assistant-history"] });
      const prev = qc.getQueryData(["assistant-history"]);
      const optimistic = { id: `tmp-${Date.now()}`, role: "user", text: message, created_at: new Date().toISOString() };
      qc.setQueryData(["assistant-history"], (old) => ({ ...(old || {}), items: [...(old?.items || []), optimistic] }));
      return { prev };
    },
    onSuccess: (res) => {
      // Reflect the fresh quota instantly, then refetch the thread.
      if (res && typeof res.quota_remaining !== "undefined") {
        qc.setQueryData(["assistant-history"], (old) => (old ? { ...old, quota_remaining: res.quota_remaining } : old));
      }
      qc.invalidateQueries({ queryKey: ["assistant-history"] });
    },
    onError: (e, _m, ctx) => {
      if (ctx?.prev) qc.setQueryData(["assistant-history"], ctx.prev);
      if (e?.response?.status === 429) toast.error("Daily AI limit reached — try again tomorrow.");
      else toast.error(errToText(e, "Assistant couldn't respond."));
    },
  });

  const clearMut = useMutation({
    mutationFn: () => api.assistantClear(),
    onSuccess: () => {
      qc.setQueryData(["assistant-history"], { items: [] });
      setConfirmClear(false);
      toast.success("Conversation cleared");
    },
    onError: () => toast.error("Couldn't clear the conversation"),
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [items.length, sendMut.isPending]);

  const submit = (e) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || sendMut.isPending || outOfQuota) return;
    setText("");
    sendMut.mutate(t);
  };

  const ask = (q) => {
    if (!sendMut.isPending && !outOfQuota) sendMut.mutate(q);
  };

  const empty = !isLoading && items.length === 0 && !sendMut.isPending;

  return (
    <div className="flex h-full min-h-0 flex-col bg-bloq-bg" data-testid="assistant-page">
      {/* Header */}
      <div className="flex shrink-0 items-center gap-3 border-b border-white/8 bg-bloq-s1/60 px-5 py-3 backdrop-blur-xl">
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-bloq-mint/12 text-bloq-mint">
          <Bot className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 font-semibold">
            BLOQ Assistant
            <span className="rounded-full bg-bloq-mint/12 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-bloq-mint">AI</span>
          </div>
          <div className="truncate text-xs text-bloq-muted">Your on-app helper for BLOQ, Web3 & safety</div>
        </div>
        {typeof quota === "number" && (
          <span
            className={cn(
              "flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold",
              outOfQuota ? "bg-amber-400/15 text-amber-300" : "bg-bloq-mint/12 text-bloq-mint",
            )}
            data-testid="assistant-quota"
            title="Your remaining daily AI messages"
          >
            <Gauge className="h-3 w-3" /> {outOfQuota ? "No AI left today" : `${quota} left`}
          </span>
        )}
        {items.length > 0 && (
          <button
            onClick={() => setConfirmClear(true)}
            className="flex h-9 w-9 items-center justify-center rounded-full text-bloq-muted transition-colors hover:bg-destructive/15 hover:text-destructive"
            data-testid="assistant-clear-btn"
            title="Clear conversation"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-5">
        {isLoading ? (
          <div className="flex h-40 items-center justify-center text-bloq-muted">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : empty ? (
          <div className="mx-auto flex max-w-md flex-col items-center px-4 py-8 text-center">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-3xl bg-bloq-mint/12 text-bloq-mint">
              <Bot className="h-8 w-8" />
            </div>
            <h2 className="text-lg font-semibold">Ask BLOQ anything</h2>
            <p className="mt-1 text-sm text-bloq-muted">
              Get help with the app, Web3 basics, and spotting scams. The Assistant never reads your encrypted chats.
            </p>
            <div className="mt-6 grid w-full gap-2">
              {STARTERS.map((s) => (
                <button
                  key={s}
                  onClick={() => ask(s)}
                  className="rounded-2xl border border-white/10 bg-bloq-s1 px-4 py-3 text-left text-sm text-white/90 transition-colors hover:border-bloq-mint/40 hover:bg-white/5"
                  data-testid={`assistant-starter-${STARTERS.indexOf(s)}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {items.map((m) => (
              <Bubble key={m.id} role={m.role} text={m.text} />
            ))}
            {sendMut.isPending && (
              <div className="flex justify-start" data-testid="assistant-typing">
                <div className="flex items-center gap-2 rounded-2xl rounded-bl-md border border-white/8 bg-bloq-s2 px-4 py-2.5 text-sm text-bloq-muted">
                  <Loader2 className="h-4 w-4 animate-spin text-bloq-mint" /> Thinking…
                </div>
              </div>
            )}
          </>
        )}
        <div ref={bottomRef} />
      </div>

      {outOfQuota && (
        <div className="mx-5 mb-2 flex shrink-0 flex-col items-center gap-2 rounded-xl border border-amber-400/25 bg-amber-400/10 px-3 py-2.5 text-center" data-testid="assistant-quota-reset">
          <div className="flex items-center gap-1.5 text-xs text-amber-300">
            <Gauge className="h-3.5 w-3.5" /> You've used today's free AI messages{resetAt ? ` — resets ${timeUntil(resetAt)}` : ""}.{canUpgrade ? "" : " Back tomorrow! ⚡"}
          </div>
          {canUpgrade && (
            <Link
              to="/app/membership"
              className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-bloq-mint to-bloq-blue px-3.5 py-1.5 text-xs font-bold text-bloq-bg transition-opacity hover:opacity-90"
              data-testid="assistant-upgrade-btn"
            >
              <Crown className="h-3.5 w-3.5" /> Upgrade for unlimited AI
            </Link>
          )}
        </div>
      )}

      {/* Composer */}
      <form onSubmit={submit} className="flex shrink-0 items-center gap-2 border-t border-white/8 bg-bloq-s1/60 px-5 py-3 backdrop-blur-xl" data-testid="assistant-composer">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={outOfQuota ? "You've used today's AI messages — back tomorrow" : "Ask the BLOQ Assistant…"}
          disabled={outOfQuota}
          className="h-11 flex-1 rounded-full border border-white/10 bg-bloq-bg px-4 text-[15px] text-white outline-none placeholder:text-bloq-muted focus:border-bloq-mint/40 disabled:opacity-50"
          data-testid="assistant-input"
        />
        <button
          type="submit"
          disabled={!text.trim() || sendMut.isPending || outOfQuota}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bloq-gradient text-bloq-bg transition-opacity hover:opacity-90 disabled:opacity-40"
          data-testid="assistant-send-btn"
        >
          {sendMut.isPending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        </button>
      </form>

      <div className="flex shrink-0 items-center justify-center gap-1 pb-2 text-[11px] text-bloq-muted">
        <ShieldCheck className="h-3 w-3 text-bloq-mint" /> The Assistant can't read your end-to-end encrypted messages.
      </div>

      <Dialog open={confirmClear} onOpenChange={setConfirmClear}>
        <DialogContent className="max-w-sm border-white/10 bg-bloq-s1 text-white" data-testid="assistant-clear-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Clear conversation?</DialogTitle>
            <DialogDescription className="text-bloq-muted">
              This deletes your Assistant history. This can't be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmClear(false)}
              className="rounded-full border-white/15 bg-transparent text-white hover:bg-white/5"
              data-testid="assistant-clear-cancel"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => clearMut.mutate()}
              disabled={clearMut.isPending}
              className="rounded-full font-bold"
              data-testid="assistant-clear-confirm"
            >
              {clearMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Clear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
